import React from "react";
import httpClient from "../axios";
import { notifyError } from "../helper";

/**
 * Loads a paginated Laravel resource collection and exposes search + pagination state.
 *
 * Laravel's paginator returns either { data, meta: { current_page, per_page, total } }
 * (resource collections) or { data, current_page, per_page, total } (plain paginate()).
 */
export default function usePagedList(endpoint, { params = {}, pageSize = 10, mapRow = (r) => r } = {}) {
	const http = React.useMemo(() => httpClient(), []);
	const [rows, setRows] = React.useState([]);
	const [loading, setLoading] = React.useState(false);
	const [search, setSearch] = React.useState("");
	const [pagination, setPagination] = React.useState({ page: 1, pageSize, total: 0 });
	const paramsKey = JSON.stringify(params);

	const load = React.useCallback(
		(page = 1, limit = pageSize, term = search) => {
			setLoading(true);
			http
				.get(endpoint, { params: { ...JSON.parse(paramsKey), page, limit, search: term || undefined } })
				.then((response) => {
					const body = response.data || {};
					const meta = body.meta || body;
					setRows((body.data || []).map(mapRow));
					setPagination({
						page: Number(meta.current_page) || page,
						pageSize: Number(meta.per_page) || limit,
						total: Number(meta.total) || 0,
					});
				})
				.catch((error) => notifyError(error, "Could not load data"))
				.finally(() => setLoading(false));
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[endpoint, paramsKey, pageSize, search]
	);

	React.useEffect(() => {
		load(1, pageSize, search);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [endpoint, paramsKey, search]);

	const onSearch = (term) => {
		setSearch(term || "");
	};

	const onPageChange = (page, limit) => load(page, limit, search);

	return {
		rows,
		loading,
		search,
		onSearch,
		reload: () => load(pagination.page, pagination.pageSize, search),
		pagination: { ...pagination, onChange: onPageChange },
	};
}
